/**
 * URL-synced filter state: every page's filters live in the query string, so a
 * refreshed page (or a shared link) restores the view exactly.
 */

import { useCallback, useTransition } from 'react';
import { useSearchParams } from 'react-router';

export type ParamValue = string | number | boolean | null | undefined;

/**
 * Pure half of the hook (so it is unit-testable): merges `next` into `current`.
 * Setting a value to `undefined`/`null`/`''` removes the key, which keeps the
 * URL equal to its default-state form.
 */
export function applyParamChanges(
  current: URLSearchParams,
  next: Record<string, ParamValue>,
): URLSearchParams {
  const draft = new URLSearchParams(current);
  for (const [key, value] of Object.entries(next)) {
    if (value === undefined || value === null || value === '') draft.delete(key);
    else draft.set(key, String(value));
  }
  return draft;
}

/**
 * Reads/writes a set of query params. Replaces the history entry by default:
 * a filter change is not a navigation the user wants to step back through.
 */
export function useQueryParams(): {
  params: Record<string, string>;
  setParams: (next: Record<string, ParamValue>, options?: { replace?: boolean }) => void;
  reset: () => void;
  isPending: boolean;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isPending, startTransition] = useTransition();

  const params: Record<string, string> = {};
  for (const [key, value] of searchParams.entries()) params[key] = value;

  const setParams = useCallback(
    (next: Record<string, ParamValue>, options?: { replace?: boolean }) => {
      startTransition(() => {
        setSearchParams(applyParamChanges(searchParams, next), {
          replace: options?.replace ?? true,
        });
      });
    },
    [searchParams, setSearchParams],
  );

  const reset = useCallback(() => {
    startTransition(() => {
      setSearchParams(new URLSearchParams(), { replace: true });
    });
  }, [setSearchParams]);

  return { params, setParams, reset, isPending };
}

/** Coerces an optional string param to a positive integer (else `undefined`). */
export function toInt(value: string | undefined): number | undefined {
  if (value === undefined || value === '') return undefined;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}
